import { BackendUser } from "../user/userService";
import { hasAccountSetting } from "./accountSettingsUtils";
import { hasPermission } from "./permissionUtils";

// Super admin access. The SERVER decides — a root account (its SUPER_ADMIN_USERS env list) or team
// staff created under one (`isSuperAdminUser` in server/auth/auth.ts) — and reports it on
// /api/users/me. There is no id list in the frontend: new team staff need no rebuild.
export const canAccessSuperAdminDashboard = (user: BackendUser) => {
    return Boolean(user.is_super_admin);
}

/**
 * Is the REAL person a super admin? Unlike `canAccessSuperAdminDashboard` this stays true inside an
 * impersonated client view, where `backendUser` is the client. Mirrors the server's
 * real-identity check. Use `canUseSuperAdminFeatures` for operational UI gates.
 */
export const isSuperAdminIdentity = (user: BackendUser | null) => {
    return Boolean(user?.is_super_admin_identity);
}

/** Hide operational admin extras in customer view without changing the real identity. */
export const canUseSuperAdminFeatures = (user: BackendUser | null) =>
    isSuperAdminIdentity(user) && user?.impersonation_view !== 'customer';

/** Preview rollout; keep the gate separate from the customer-ready overview. */
export const canAccessSetupOverview = (user: BackendUser | null, impersonationUserId: string | null) =>
    Boolean(impersonationUserId && user?.id === impersonationUserId && canUseSuperAdminFeatures(user));

/**
 * A mode switch reloads the current URL. Leave internal/force-enabled pages if
 * the customer cannot open them. Ordinary logins keep their existing routing.
 * Setup and onboarding deliberately retain the usual impersonation behavior.
 */
export const canAccessImpersonationCustomerPath = (
    user: BackendUser | null, accountSettings: string[], pathname: string
): boolean => {
    if (!isSuperAdminIdentity(user) || user?.impersonation_view !== 'customer') return true;
    if (/^\/(?:admin|setup-overview|testInvoices)(?:\/|$)/.test(pathname)) return false;
    if (/^\/analysis\/(?:waste|money-savers)(?:\/|$)/.test(pathname)) return false;
    if (/^\/analysis(?:\/|$)/.test(pathname)) return canAccessAnalysisPage(user, accountSettings);
    if (/^\/reports(?:\/|$)/.test(pathname)) return canAccessReportsPage(user, accountSettings);
    if (/^\/menus\/change-(?:dashboard|setup)(?:\/|$)/.test(pathname)) {
        return canAccessMenuChangeDashboard(user, accountSettings);
    }
    return true;
};

// ============================================================================
// ACCOUNT SETTINGS-BASED FUNCTIONS (System 1)
// ============================================================================

/**
 * Page access = account-level setting (super-admin gate, inherited by staff from
 * their admin) AND a role/permission check. Admins pass whenever the account has
 * the page; staff additionally need the page's edit permission. This is what lets
 * an admin delegate a page to specific staff without opening it to everyone.
 */
const canAccessPage = (
    settingKey: string,
    permissionKey: string,
    user: BackendUser | null,
    accountSettings: string[]
): boolean => {
    if (!user) return false;
    if (!accountSettings.includes(settingKey)) return false;
    if (user.role === 'admin') return true;
    return hasPermission(permissionKey, user);
}

/**
 * Maps each page's account setting to the staff permission that unlocks it.
 * Used by the Staff page to only show a permission toggle when the account
 * actually has the corresponding page.
 */
export const PAGE_PERMISSION_BY_SETTING: Record<string, string> = {
    mep_page: 'mep_edit',
    checklists_page: 'checklist_edit',
    inventory_sections_page: 'inventory_edit',
    recipes_page: 'recipe_edit',
    notifications_page: 'notifications_edit',
    waste_page: 'waste_edit',
    menu_change_dashboard: 'menu_change_dashboard',
};

/**
 * Check if user can access MEP page
 */
export const canAccessMepPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('mep_page', 'mep_edit', user, accountSettings);
}

/**
 * Check if user can access Checklists page
 */
export const canAccessChecklistsPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('checklists_page', 'checklist_edit', user, accountSettings);
}

/**
 * Check if user can access Inventory page
 */
export const canAccessInventoryPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('inventory_sections_page', 'inventory_edit', user, accountSettings);
}

/**
 * Check if user can export the inventory count review as a file. Account-level
 * feature toggle, admin-only (the setting is in ADMIN_ONLY_SETTINGS, so staff
 * never pass even when the account has it). Impersonating super admins bypass
 * this check in the UI.
 */
export const canExportInventoryCountReview = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('inventory_count_export', user, accountSettings);
}

/**
 * Check if user can access Recipes page
 */
export const canAccessRecipesPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('recipes_page', 'recipe_edit', user, accountSettings);
}

/**
 * Check if user can access Notifications page
 */
export const canAccessNotificationsPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('notifications_page', 'notifications_edit', user, accountSettings);
}

/**
 * Whether this is an "invoice-only" account. `invoices_page` is not a page toggle
 * but a whole-app mode: the account sees ONLY Home, Upload invoices and Orders.
 *
 * This OVERRIDES every other account setting — including the ones the backend
 * force-enables while a super admin impersonates (IMPERSONATION_FORCED_SETTINGS),
 * so an impersonating super admin sees exactly the nav the account itself gets.
 * Nav callers must check this FIRST and skip their normal per-page gating.
 */
export const isInvoiceOnlyAccount = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('invoices_page', user, accountSettings);
}

/**
 * Check if user can access Invoices page. Same flag as isInvoiceOnlyAccount — the
 * accounts that reach /invoices are exactly the invoice-only ones.
 */
export const canAccessInvoicesPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return isInvoiceOnlyAccount(user, accountSettings);
}

/**
 * Check if user can access Reports page.
 * Account-setting only (invoices_page pattern): admin + all staff of the account
 * inherit it. Super admins always see it while impersonating (reports_page is in
 * IMPERSONATION_FORCED_SETTINGS server-side).
 */
export const canAccessReportsPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('reports_page', user, accountSettings);
}

/**
 * Check if user can access the Cost Percent (Inslag) page.
 * Account-setting only (admin + all staff of the account inherit it), same
 * pattern as Reports. Behind a per-account flag while we test it; super admins
 * always see it while impersonating (cost_percent_page is in
 * IMPERSONATION_FORCED_SETTINGS server-side).
 */
export const canAccessCostPercentPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('cost_percent_page', user, accountSettings);
}

/**
 * Check if user can access the Analysis page (action items, overviews, on-demand reports).
 * Account-setting only, admin-only (analysis_page is in ADMIN_ONLY_SETTINGS). Off for every
 * customer in v1; super admins always see it while impersonating (analysis_page is in
 * IMPERSONATION_FORCED_SETTINGS server-side).
 */
export const canAccessAnalysisPage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('analysis_page', user, accountSettings);
}

/**
 * Check if user can access the Menu Change Dashboard.
 * Requires the account feature plus the staff permission (or all_permissions).
 * Access includes managing rollouts and the account's shared templates.
 */
export const canAccessMenuChangeDashboard = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('menu_change_dashboard', 'menu_change_dashboard', user, accountSettings);
}

/** Support is always available to eligible users, including invoice-only/unpaid accounts. */
export const canAccessSupportPage = (user: BackendUser | null): boolean => {
    if (!user || !['admin', 'staff'].includes(user.role)) return false;
    if (user.role === 'staff' && (user.access_request === 'pending' || user.access_request === 'rejected')) return false;
    return true;
}

/**
 * Check if account has dashboard food-only setting (Ayla-specific)
 */
export const hasDashboardFoodOnly = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('dashboard_food_only', user, accountSettings);
}

/**
 * Check if user can access demo account features (clear data buttons, edit quantities)
 */
export const canAccessDemoAccount = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('demo_account', user, accountSettings);
}

/**
 * Check if user can correct/override parsed quantity info from the order detail page.
 * Admin-only (gates only the edit modal's accessibility; no backend logic depends on it).
 */
export const canEditQuantityInfoOverride = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('edit_quantity_info_override', user, accountSettings);
}

/**
 * Check if user can access cost price automation tools
 */
export const canAccessCostPriceAutomation = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('cost_price_automation', user, accountSettings);
}

/**
 * Check if user can access recipe automation tools
 */
export const canAccessRecipeAutomation = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('recipe_automation', user, accountSettings);
}

/**
 * Check if user can access Waste Tracking page
 */
export const canAccessWastePage = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('waste_page', 'waste_edit', user, accountSettings);
}

/**
 * Check if user can access sourcing improvement tools. Account-level setting +
 * admin, or staff explicitly granted the `sourcing_improvement` permission.
 */
export const canAccessSourcingImprovement = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canAccessPage('sourcing_improvement', 'sourcing_improvement', user, accountSettings);
}

/**
 * Check if user can access the Compare Supplier flow (upload a new-supplier price
 * file and compare it against the period's order-history products). Admin-only,
 * same concept as sourcing improvement.
 */
export const canAccessSupplierComparison = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('supplier_comparison', user, accountSettings);
}

/**
 * Check if user can access the "Check categories" flow on the products page (a second AI model
 * audits the stored product categories and proposes fixes). Admin-only: the check spends AI budget
 * and the apply rewrites categories across the account's whole order history.
 */
export const canAccessCategoryCheck = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('category_check', user, accountSettings);
}

/**
 * Check if account has free subscription (bypass subscription requirement)
 */
export const hasFreeSubscription = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('free_subscription', user, accountSettings);
}

/**
 * Check if account can see the dashboard budget reminder label (admin + staff)
 */
export const canAccessBudgetReminder = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('budget_reminder', user, accountSettings);
}

/**
 * Dashboard stock-adjusted cost %: super-admins (not in customer view) while testing,
 * customers only once the account toggle is on. Server gate: expectedInventoryRoutes GET.
 */
export const canAccessInventoryAdjustedCostPercent = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return canUseSuperAdminFeatures(user) || hasAccountSetting('inventory_adjusted_cost_percent', user, accountSettings);
}

/**
 * Check if the account can import custom ingredients from supplier price files
 * (batch upload). Available to admin AND staff when the account has the toggle.
 */
export const canAccessFileBatchCustomIngredients = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('file_batch_custom_ingredients', user, accountSettings);
}

/**
 * Check if the account can bulk-create subrecipes from an uploaded template
 * file. Available to admin AND staff when the account has the toggle.
 */
export const canAccessFileBatchSubrecipes = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('file_batch_subrecipes', user, accountSettings);
}

/**
 * Check if the account can bootstrap inventory structure + counts from an
 * uploaded Excel file (one-time importer). Available to admin AND staff when the
 * account has the toggle.
 */
export const canAccessInventoryImport = (user: BackendUser | null, accountSettings: string[]): boolean => {
    return hasAccountSetting('inventory_import_automation', user, accountSettings);
}

// ============================================================================
// PERMISSION-BASED FUNCTIONS (System 2)
// ============================================================================

/**
 * Check if user can access Staff page
 * Returns true for admin OR staff with manage_staff permission
 */
export const canAccessStaffPage = (user: BackendUser | null): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return hasPermission('manage_staff', user);
}

/**
 * Check if user can manage staff (approve/reject, update access, etc.)
 * Same as canAccessStaffPage but more semantic name for action checks
 */
export const canManageStaff = (user: BackendUser | null): boolean => {
    return canAccessStaffPage(user);
}

/**
 * Whether `user` is "super-staff": has manage_staff or all_permissions.
 * (Mirrors the backend isSuperStaff check.)
 */
const isSuperStaff = (perms: string[] | null | undefined): boolean => {
    const p = perms || [];
    return p.includes('manage_staff') || p.includes('all_permissions');
}

/**
 * Whether the current user may manage a specific staff member (edit role,
 * permissions, access, add/remove restaurant, delete, approve/reject).
 *
 * - Admin: always (the list is already scoped to their own staff).
 * - Super-staff: only if the target is NOT itself super-staff, is not the actor,
 *   and the target's restaurants are a subset of the actor's restaurant access.
 * - Lower staff: never.
 *
 * `actorRestaurantIds` is the set of restaurant IDs the current user has access to
 * (from the restaurant store, which is already scoped per user).
 * Mirrors the backend canManageStaff predicate.
 */
export const canManageStaffMember = (
    user: BackendUser | null,
    target: { id: string; permissions?: string[] | null; restaurant_ids: string[] },
    actorRestaurantIds: string[]
): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!isSuperStaff(user.permissions)) return false;
    if (target.id === user.id) return false;
    if (isSuperStaff(target.permissions)) return false;
    if (target.restaurant_ids.length === 0) return false;
    const actorSet = new Set(actorRestaurantIds);
    return target.restaurant_ids.every(rid => actorSet.has(rid));
}
